/**
 * PlanEditor — structured plan editor with char count +
 * auto-save + last-saved indicator (issue #437 acceptance
 * criterion #5).
 *
 * The plan is structured (warm-up + activities + prompts +
 * closing + parent update) rather than a single textarea. The
 * char count is the combined length of all editable fields
 * capped at 5000 chars (per AC #5).
 *
 * Auto-save fires 2s after the SLP stops typing (delegated to
 * the parent via the `onAutoSave` callback which fires when the
 * 2s debounce elapses). The "Last saved Xs ago" indicator
 * updates every 5s via an internal interval (AC #5). When the
 * editor has unpersisted edits the indicator shows "Unsaved
 * changes".
 *
 * `PlanEditor` is a controlled component: the parent owns
 * `value` and supplies field-level change handlers. Auto-save
 * is opt-in via `onAutoSave` — the parent decides what to
 * persist (typically the existing save endpoint that
 * `SessionPlannerScreen.handleSavePlan` already calls).
 */
import { useEffect, useRef, useState } from 'react';
import type { FocusEvent, ReactElement } from 'react';
import { useAutoSave } from '../lib/session/autoSave';

export interface PlanActivity {
  name: string;
  duration_min: number;
  cues: string;
  materials: string;
}

export interface PlanEditorValue {
  warmup: string;
  activities: PlanActivity[];
  prompts: string[];
  closing: string;
  parentUpdate: string;
}

export interface PlanEditorProps {
  value: PlanEditorValue;
  /** Called whenever any field changes — parent updates state. */
  onChange: (next: PlanEditorValue) => void;
  /**
   * Called when the 2s debounce elapses with the latest value.
   * Should perform the network save (and resolve when done).
   */
  onAutoSave: (value: PlanEditorValue) => Promise<void>;
  /** Disable auto-save + field interactions while read-only. */
  readOnly?: boolean;
  /**
   * Hide the editor (e.g. when the parent is rendering the read-only
   * Preview view in parallel). When `hidden` flips from `true` back
   * to `false` the editor restores focus + selection to the last
   * focused textarea/input so the SLP resumes typing where they
   * left off (issue #437 acceptance criterion #6).
   */
  hidden?: boolean;
  /** Called when an activity should be removed. */
  onRemoveActivity?: (idx: number) => void;
  /** Called when a prompt should be removed. */
  onRemovePrompt?: (idx: number) => void;
  /** Called when the SLP taps "+ Add prompt". */
  onAddPrompt?: () => void;
}

const MAX_CHARS = 5000;
const SAVED_INDICATOR_REFRESH_MS = 5000;

function totalCharCount(value: PlanEditorValue): number {
  let n = value.warmup.length + value.closing.length + value.parentUpdate.length;
  for (const a of value.activities) {
    n += a.name.length + a.cues.length + a.materials.length;
  }
  for (const p of value.prompts) n += p.length;
  return n;
}

/**
 * Enforce the combined 5000-character cap. Allows deletions even
 * when the plan is already over the limit so the SLP can recover
 * from a paste that's too large.
 */
function withinCharLimit(current: PlanEditorValue, next: PlanEditorValue): boolean {
  const currentChars = totalCharCount(current);
  const nextChars = totalCharCount(next);
  return nextChars <= MAX_CHARS || nextChars <= currentChars;
}

/**
 * Format the time-since-last-save as a short human label
 * ("3s ago", "1m ago", "2h ago") for the "Last saved Xs ago"
 * indicator.
 */
export function formatSavedAgo(now: number, then: number): string {
  const deltaMs = Math.max(0, now - then);
  const sec = Math.floor(deltaMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}

export function PlanEditor({
  value,
  onChange,
  onAutoSave,
  readOnly = false,
  hidden = false,
  onRemoveActivity,
  onRemovePrompt,
  onAddPrompt,
}: PlanEditorProps): ReactElement {
  // The auto-save hook owns the 2s debounce + reactive state.
  // When readOnly is true we still need to satisfy the type
  // signature, so we pass `value` but disable scheduling via
  // `enabled: !readOnly`.
  const auto = useAutoSave<PlanEditorValue>({
    content: value,
    save: onAutoSave,
    delayMs: 2000,
    enabled: !readOnly,
  });

  // Tick state to refresh "Last saved Xs ago" every 5s.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (readOnly) return;
    const id = window.setInterval(() => {
      setTick((n) => n + 1);
    }, SAVED_INDICATOR_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [readOnly]);

  const chars = totalCharCount(value);
  const overLimit = chars > MAX_CHARS;

  const lastSavedLabel =
    auto.status === 'pending'
      ? 'Unsaved changes'
      : auto.status === 'saving'
        ? 'Saving…'
        : auto.status === 'error'
          ? 'Save failed'
          : auto.status === 'saved' && auto.lastSavedAt != null
            ? `Last saved ${formatSavedAgo(Date.now(), auto.lastSavedAt)}`
            : 'No edits yet';

  function setField<K extends 'warmup' | 'closing' | 'parentUpdate'>(
    key: K,
    v: string,
  ): void {
    const next = { ...value, [key]: v };
    if (!withinCharLimit(value, next)) return;
    onChange(next);
  }

  function setActivityField(
    idx: number,
    field: keyof PlanActivity,
    v: string | number,
  ): void {
    const nextActivities = value.activities.map((a, i) =>
      i === idx ? { ...a, [field]: v } : a,
    );
    const next = { ...value, activities: nextActivities };
    if (!withinCharLimit(value, next)) return;
    onChange(next);
  }

  function setPromptField(idx: number, v: string): void {
    const nextPrompts = value.prompts.map((p, i) => (i === idx ? v : p));
    const next = { ...value, prompts: nextPrompts };
    if (!withinCharLimit(value, next)) return;
    onChange(next);
  }

  const lastFocusRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);

  function handleFocusCapture(e: FocusEvent<HTMLDivElement>): void {
    const target = e.target as HTMLTextAreaElement | HTMLInputElement;
    if (
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'INPUT'
    ) {
      lastFocusRef.current = target;
    }
  }

  // Restore focus + selection to the last-focused field when the
  // editor transitions back to an editable / visible state. The
  // effect depends on `[readOnly, hidden]` so it fires on both
  // (a) the read-only -> edit-mode flip (Preview toggle while
  // editingPlan was false) and (b) the hidden -> visible flip
  // (Preview toggle while editingPlan stayed true — the AC #6
  // cursor-position case where `editingPlan=true` throughout so
  // `readOnly` never changes).
  useEffect(() => {
    if (!readOnly && !hidden && lastFocusRef.current) {
      const el = lastFocusRef.current;
      // Defer focus to the next frame so the DOM is fully visible
      // (e.g. the parent's display:none wrapper just flipped to
      // display:block). Without the rAF the focused textarea may
      // not be focusable yet and the focus() call silently no-ops.
      requestAnimationFrame(() => {
        if (!el.isConnected) return;
        el.focus({ preventScroll: true });
        if (typeof el.setSelectionRange === 'function') {
          const start = el.selectionStart ?? 0;
          const end = el.selectionEnd ?? 0;
          el.setSelectionRange(start, end);
        }
      });
    }
  }, [readOnly, hidden]);

  return (
    <div
      className={`plan-editor ${hidden ? 'plan-editor--hidden' : ''}`.trim()}
      data-testid="plan-editor"
      data-hidden={hidden ? 'true' : 'false'}
      aria-hidden={hidden}
      onFocusCapture={handleFocusCapture}
    >
      <fieldset className="plan-editor-fieldset" disabled={readOnly}>
        <legend>Warm-up</legend>
        <p className="plan-form-hint">
          Quick review of previous targets or a fun opener to get the
          student engaged (2-3 min).
        </p>
        <textarea
          className="plan-form-textarea"
          value={value.warmup}
          rows={3}
          placeholder="Review previous targets, quick warm-up activity…"
          onChange={(e) => setField('warmup', e.target.value)}
        />
      </fieldset>

      <fieldset className="plan-editor-fieldset" disabled={readOnly}>
        <legend>Target activities</legend>
        {value.activities.map((act, idx) => (
          <div key={idx} className="plan-activity-card">
            <div className="plan-activity-header">
              <span className="plan-activity-number">Activity {idx + 1}</span>
              {onRemoveActivity && (
                <button
                  type="button"
                  className="plan-activity-remove"
                  onClick={() => onRemoveActivity(idx)}
                >
                  Remove
                </button>
              )}
            </div>
            <div className="plan-form-field">
              <label className="plan-form-label">Name</label>
              <input
                type="text"
                className="plan-form-input"
                value={act.name}
                placeholder="Activity name"
                onChange={(e) => setActivityField(idx, 'name', e.target.value)}
              />
            </div>
            <div className="plan-form-field">
              <label className="plan-form-label">Duration (min)</label>
              <input
                type="number"
                className="plan-form-input plan-form-input--narrow"
                min={0}
                max={60}
                value={act.duration_min}
                onChange={(e) =>
                  setActivityField(
                    idx,
                    'duration_min',
                    Number.isFinite(Number(e.target.value))
                      ? Number(e.target.value)
                      : 0,
                  )
                }
              />
            </div>
            <div className="plan-form-field">
              <label className="plan-form-label">Cues</label>
              <textarea
                className="plan-form-textarea"
                value={act.cues}
                rows={2}
                placeholder="Verbal model, visual cue, tactile prompt…"
                onChange={(e) => setActivityField(idx, 'cues', e.target.value)}
              />
            </div>
            <div className="plan-form-field">
              <label className="plan-form-label">Materials</label>
              <textarea
                className="plan-form-textarea"
                value={act.materials}
                rows={2}
                placeholder="Picture cards, mirror, game board…"
                onChange={(e) => setActivityField(idx, 'materials', e.target.value)}
              />
            </div>
          </div>
        ))}
      </fieldset>

      <fieldset className="plan-editor-fieldset" disabled={readOnly}>
        <legend>What to track</legend>
        <p className="plan-form-hint">
          What to record during the session — one per activity.
        </p>
        {value.prompts.map((p, idx) => (
          <div key={idx} className="plan-prompt-row">
            <textarea
              className="plan-form-textarea"
              value={p}
              rows={2}
              placeholder="e.g. Tally correct /r/ at word level, 80% accuracy"
              onChange={(e) => setPromptField(idx, e.target.value)}
            />
            {onRemovePrompt && value.prompts.length > 1 && (
              <button
                type="button"
                className="plan-prompt-remove"
                onClick={() => onRemovePrompt(idx)}
                aria-label="Remove prompt"
              >
                ×
              </button>
            )}
          </div>
        ))}
        {onAddPrompt && (
          <button
            type="button"
            className="link-button plan-prompt-add"
            onClick={onAddPrompt}
          >
            + Add prompt
          </button>
        )}
      </fieldset>

      <fieldset className="plan-editor-fieldset" disabled={readOnly}>
        <legend>Closing</legend>
        <p className="plan-form-hint">
          Wrap-up, reinforcement, and transition out of the session.
        </p>
        <textarea
          className="plan-form-textarea"
          value={value.closing}
          rows={3}
          placeholder="Wrap-up, reinforcement, transition to next activity…"
          onChange={(e) => setField('closing', e.target.value)}
        />
      </fieldset>

      <fieldset className="plan-editor-fieldset" disabled={readOnly}>
        <legend>Parent update prompt</legend>
        <p className="plan-form-hint">
          What to tell the parent — what you worked on, how it went,
          and what to practice at home.
        </p>
        <textarea
          className="plan-form-textarea"
          value={value.parentUpdate}
          rows={3}
          placeholder="What to tell the parent about today's session…"
          onChange={(e) => setField('parentUpdate', e.target.value)}
        />
      </fieldset>

      {/* Character count + saved-status row lives below the editor
          fields (issue #437 acceptance criterion #5). */}
      <div className="plan-editor-meta">
        <span
          className={`plan-editor-charcount ${overLimit ? 'plan-editor-charcount--over' : ''}`}
          data-testid="plan-editor-charcount"
        >
          {chars} / {MAX_CHARS}
        </span>
        <span
          className={`plan-editor-savedstatus plan-editor-savedstatus--${auto.status}`}
          data-testid="plan-editor-savedstatus"
        >
          {lastSavedLabel}
        </span>
      </div>
    </div>
  );
}
